/**
 * 从 AI 回复中解析"可交互表格"结构
 *
 * 触发条件（满足任意一项即可）：
 * 1. 表格任意列标题含有交互关键词（待确认/请补充/需要确认/信息点/选项等）
 * 2. 消息正文含有"请用户填写/确认信息"的上下文短语
 *
 * 支持表格格式：
 * - 2列：col1=需求项, col2=说明/选项
 * - 3列且 col1 是数字序号：col1 忽略, col2=需求项, col3=说明/选项
 * - 3列且 col1 非数字：col1=需求项, col2=说明/选项, col3 忽略
 *
 * 每行交互类型推断（优先级从高到低）：
 * - 含有 "1." "2." 等编号选项 → 'select'（单选按钮组）
 * - 含有 "是否"/"同意"/"推荐" 等 → 'yesno'（[是]/[否] 按钮）
 * - 其他 → 'text'（自由输入框）
 */

// ─── 类型定义 ─────────────────────

/** 编号选项（如 "1. 纯扁平化…" → { index: "1", label: "纯扁平化…" }） */
export interface NumberedOption {
  readonly index: string;
  readonly label: string;
}

/** 交互表格中的单行数据 */
export interface TableInteractionRow {
  /** 需求项名称 */
  readonly itemName: string;
  /** 说明/选项描述（纯文本，<br> 已转换为换行） */
  readonly contentHint: string;
  /** 推断出的交互类型 */
  readonly inputType: 'yesno' | 'text' | 'select';
  /** inputType === 'select' 时，解析出的编号选项列表 */
  readonly options?: readonly NumberedOption[];
}

/** 解析出的可交互表格数据 */
export interface TableInteractionData {
  readonly rows: readonly TableInteractionRow[];
}

// ─── 内部工具 ─────────────────────

/**
 * 表头关键词：某列标题含有这些词，说明该表需要用户填写
 */
const HEADER_KEYWORDS = /待确认|请补充|请说明|请填写|待填写|需要确认|确认.*信息|说明[/／]选项|信息点|选项|确认内容|确认项|您的选择/;

/**
 * 消息正文上下文关键词（兜底检测）
 * 当表头未命中时，若消息内容明确邀请用户填写/确认，也触发交互
 */
const MESSAGE_CONTEXT_KEYWORDS = /请.*确认以下|请根据.*确认|请.*提供以下|请.*回答.*问题|请.*补充.*以下|确认以下.*信息|以下.*需.*确认|以下.*关键信息|请.*确认.*细节|请.*核实|请.*说明.*以下/;

/** 判断内容是否为 yes/no 型问题 */
const isYesNoQuestion = (content: string): boolean =>
  /是否|同意|需要[^，,。\n]{0,10}[？?]|推荐/.test(content);

/** 编号选项正则：匹配 "1. xxx" 或 "1、xxx" */
const NUMBERED_OPTION_RE = /(?:^|\n)\s*(\d+)[.、]\s*(.+?)(?=\n\s*\d+[.、]|$)/gs;

/**
 * 检测并提取编号选项列表
 * 返回 null 表示内容不含编号选项
 */
const parseNumberedOptions = (text: string): readonly NumberedOption[] | null => {
  const options: NumberedOption[] = [];
  NUMBERED_OPTION_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = NUMBERED_OPTION_RE.exec(text)) !== null) {
    options.push({ index: m[1], label: m[2].trim() });
  }
  return options.length >= 2 ? options : null;
};

/**
 * 去除行内 markdown 格式 + HTML <br> 转换为换行
 */
const cleanCellText = (text: string): string =>
  text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/`(.+?)`/g, '$1');

/**
 * 判断是否为分隔行（单元格值仅由 :、- 和空格组成）
 */
const isSeparatorRow = (cells: string[]): boolean =>
  cells.length > 0 && cells.every(cell => /^[\s:‐\-]*$/.test(cell));

/**
 * 判断是否为序号单元格（纯数字，如 1, 2, 3）
 */
const isIndexCell = (value: string): boolean => /^\d+$/.test(value.trim());

/**
 * 解析 markdown 表格，返回所有有效行（排除分隔行）
 */
const parseMarkdownTable = (text: string): string[][] => {
  const rows: string[][] = [];

  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) continue;

    const cells = trimmed
      .slice(1, -1)
      .split('|')
      .map(c => c.trim());

    if (cells.length < 2) continue;
    if (isSeparatorRow(cells)) continue;

    rows.push(cells);
  }

  return rows;
};

/**
 * 从表格行的单元格中提取 itemName 和 contentHint
 * - 3列且 col1 是纯数字序号 → col2=itemName, col3=contentHint
 * - 3列且 col1 非数字 → col1=itemName, col2=contentHint
 * - 2列 → col1=itemName, col2=contentHint
 */
const extractRowData = (cells: string[]): { itemName: string; contentHint: string } => {
  if (cells.length >= 3 && isIndexCell(cells[0])) {
    return {
      itemName: cleanCellText(cells[1] ?? ''),
      contentHint: cleanCellText(cells[2] ?? ''),
    };
  }
  return {
    itemName: cleanCellText(cells[0] ?? ''),
    contentHint: cleanCellText(cells[1] ?? ''),
  };
};

/**
 * 推断行的交互类型（优先级：select > yesno > text）
 */
const inferInputType = (
  contentHint: string,
): { inputType: 'yesno' | 'text' | 'select'; options?: readonly NumberedOption[] } => {
  // 优先检测编号选项（1. xxx  2. xxx）
  const opts = parseNumberedOptions(contentHint);
  if (opts) return { inputType: 'select', options: opts };

  // 其次检测 yes/no
  if (isYesNoQuestion(contentHint)) return { inputType: 'yesno' };

  return { inputType: 'text' };
};

// ─── 主函数 ─────────────────────

/**
 * 从 AI 消息内容中提取可交互表格。
 * 若消息不含可交互表格则返回 null。
 */
export const extractInteractiveTable = (content: string): TableInteractionData | null => {
  if (!content || !content.includes('|')) return null;

  const rows = parseMarkdownTable(content);
  if (rows.length < 2) return null;

  const headerRow = rows[0];
  const dataRows = rows.slice(1);
  if (dataRows.length === 0) return null;

  // 检查1：任意列标题含有交互关键词
  const headerMatches = headerRow.some(col => HEADER_KEYWORDS.test(col));
  // 检查2：消息正文含有"请用户填写"的上下文（兜底）
  const contextMatches = MESSAGE_CONTEXT_KEYWORDS.test(content);

  if (!headerMatches && !contextMatches) return null;

  return {
    rows: dataRows.map(row => {
      const { itemName, contentHint } = extractRowData(row);
      const { inputType, options } = inferInputType(contentHint);
      return { itemName, contentHint, inputType, ...(options ? { options } : {}) };
    }),
  };
};

